/**
 * Represents metadata for Ixigo's history navigation.
 */
export type IxigoHistoryMetaData = {
	ixiMeta: {
		/**
		 * Sequential index of the current URL in the browsing session on this domain.
		 */
		idx: number;

		/**
		 * Count of consecutive history entries for the current pathname, including the current entry.
		 */
		aidx: number;
	};
};

/**
 * Type guard to check if an object contains Ixigo history metadata.
 * @param state - The object to check.
 * @returns A type predicate indicating if the object is IxigoHistoryMetaData.
 */
export function hasIxigoHistoryMetaData(
	state: unknown,
): state is IxigoHistoryMetaData {
	return !!(state && typeof state === 'object' && 'ixiMeta' in state);
}

/**
 * Determines if navigation to a previous page is possible.
 * It also mimicks the behavior of `navigation.canGoBack` for browsers that do not support the `navigation` API.
 * @returns True if backward navigation is possible, false otherwise.
 */
export function canGoBack() {
	const navigation = window.navigation;
	const state = window.history.state;
	const ixiMeta = hasIxigoHistoryMetaData(state) ? state.ixiMeta : undefined;
	if (navigation) return navigation.canGoBack && (!ixiMeta || ixiMeta.idx > 0);

	if (ixiMeta) {
		return ixiMeta.idx > 0;
	}
	return false;
}

/**
 * Finds the index of the previous unique path in the navigation history.
 * @returns An array with [relativePrevIndex, currentIndex] if found, null otherwise.
 */
export function getPrevPathIndex():
	| [relativePrevIndex: number, currentIndex: number]
	| null {
	const navigation = window.navigation;
	if (!canGoBack()) return null;

	const state = window.history.state;
	const ixiMeta = hasIxigoHistoryMetaData(state) ? state.ixiMeta : undefined;

	if (navigation) {
		const currentIndex = navigation.currentEntry?.index || 0;
		let entryIndex = currentIndex - 1;
		const entries = navigation.entries();

		/*
         Search backwards for a different pathname.
         If ixiMeta is present, search backwards only within the range of ixiMeta.idx.
         This is done to achieve the scenario of marking a page as the first entry in history.
         For example, when a user lands on a confirmation page, it can be marked as the first entry,
         preventing the user from going back to the previous page using the app navigation bar.
        */
		while (
			ixiMeta
				? entryIndex >= Math.max(currentIndex - ixiMeta.idx, 0)
				: entryIndex > -1
		) {
			const pathname = new URL(
				entries[entryIndex]?.url ?? window.location.pathname,
				window.location.origin,
			).pathname;
			if (pathname !== window.location.pathname) {
				return [entryIndex - currentIndex, currentIndex];
			}
			entryIndex--;
		}
	} else if (ixiMeta && ixiMeta.idx !== ixiMeta.aidx - 1) {
		// Multiple entries exist with at least one different URL
		return [-ixiMeta.aidx, ixiMeta.idx];
	}
	return null;
}

/**
 * Attempts to navigate to the previous unique path in the history.
 * @returns True if navigation was successful, false otherwise.
 */
export function goBackToPrevPath() {
	const prevPathIndex = getPrevPathIndex();
	if (prevPathIndex) {
		history.go(prevPathIndex[0]);
		return true;
	}
	return false;
}

/**
 * it marks the current history entry as the first entry in the history
 * so that you can prevent user from going back to the previous page when they hit the back button
 * in app navigation bar
 */
export function resetHistoryIndex() {
	window.history.replaceState({ ixiMeta: { idx: 0, aidx: 1 } }, '');
}
